/**
 * Formatage des libellés d'axe temps pour les widgets graphiques (line/bar/area).
 * Porté de Metrix (`lib/chartStyle.ts` → formatDateLabel). Convertit les valeurs
 * brutes renvoyées par les connecteurs (GA4/Ads) en libellés lisibles.
 */

export type Row = Record<string, string | number>;

/** Jours de la semaine GA4 : la dimension `dayOfWeek` renvoie "0"–"6" (0 = dimanche). */
const DAYS_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

/**
 * Formate une valeur de bucket temporel en libellé court lisible.
 * `dimKey` (optionnel) lève l'ambiguïté GA4 `yearWeek` vs `yearMonth` (même format 6 chiffres)
 * et déclenche le mapping `dayOfWeek`. Sans `dimKey`, on retombe sur une heuristique.
 */
export function formatDateLabel(value: string, dimKey?: string): string {
  if (!value) return value;
  // dayOfWeek : GA4 renvoie "0"–"6". Un digit unique 0-6 ne provient que de cette dimension.
  if (dimKey === 'dayOfWeek' || (dimKey == null && /^[0-6]$/.test(value))) {
    const d = Number(value);
    if (Number.isInteger(d) && d >= 0 && d <= 6) return DAYS_FR[d];
  }
  // YYYY-MM-DD (jour, ISO)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value.slice(8, 10)}/${value.slice(5, 7)}`;
  // YYYYMMDD (GA4 jour, 8 chiffres)
  if (/^\d{8}$/.test(value)) return `${value.slice(6, 8)}/${value.slice(4, 6)}`;
  // YYYY-MM (mois, avec séparateur)
  if (/^\d{4}-\d{2}/.test(value) && value.length <= 10) return `${value.slice(5, 7)}/${value.slice(2, 4)}`;
  // YYYYWW / YYYYMM (GA4, 6 chiffres) — désambiguïsé par dimKey, sinon heuristique (2 derniers > 12 → semaine)
  if (/^\d{6}$/.test(value)) {
    const ww = value.slice(4, 6);
    if (dimKey === 'yearWeek') return `S${ww}`;
    if (dimKey === 'yearMonth') return `${ww}/${value.slice(2, 4)}`;
    return parseInt(ww, 10) > 12 ? `S${ww}` : `${ww}/${value.slice(2, 4)}`;
  }
  // YYYY-TN (trimestre)
  if (/^\d{4}-T\d$/.test(value)) return value;
  return value;
}

/**
 * Détermine le champ à utiliser comme axe X (temps/dimension) pour un graphique :
 * la dimension explicite si présente, sinon le premier champ des lignes qui n'est
 * pas une métrique (cas où l'axe temps vient de la granularité, ajoutée côté serveur).
 */
/** Champs temporels ajoutés par la granularité, cibles des `granularity_map` GA4 (`date`,
 *  `yearWeek`, `yearMonth`) ET Google Ads (`date`, `week`, `month`). Prioritaires comme axe X
 *  d'une série temporelle : si présents, on les utilise même si une dimension est sélectionnée. */
const TIME_FIELDS = ['date', 'yearWeek', 'yearMonth', 'week', 'month'];

/**
 * Vrai si la clé d'axe X est un champ temporel (issu de la granularité) et non une
 * dimension catégorielle. Sert à décider si un top-N a du sens : tronquer un axe temps
 * amputerait la série (30 jours réduits à 10 points), tronquer un axe catégoriel non.
 */
export function isTimeDimensionKey(dimKey: string | undefined): boolean {
  return !!dimKey && TIME_FIELDS.includes(dimKey)
}

export function resolveDimensionKey(
  rows: Row[],
  explicitDimensions: string[] | undefined,
  metrics: string[],
): string | undefined {
  const first = rows[0];
  if (first) {
    const timeField = TIME_FIELDS.find((t) => t in first);
    if (timeField) return timeField;
  }
  if (explicitDimensions && explicitDimensions.length) return explicitDimensions[0];
  if (!first) return undefined;
  const metricSet = new Set(metrics);
  return Object.keys(first).find((k) => !metricSet.has(k));
}

/** Trie les lignes par la valeur du champ temps/dimension (ordre chronologique). */
export function sortByDimension(rows: Row[], dimKey: string | undefined): Row[] {
  if (!dimKey) return rows;
  return [...rows].sort((a, b) => {
    const av = String(a[dimKey] ?? '');
    const bv = String(b[dimKey] ?? '');
    return av < bv ? -1 : av > bv ? 1 : 0;
  });
}

/**
 * Détermine si `explicitDimensions[0]` doit être traité comme une segmentation en
 * séries (une courbe par valeur) plutôt que comme l'axe X du graphique.
 *
 * Le backend combine granularité + dimension en une seule requête et renvoie des
 * lignes plates `{date, dimension, métrique}`. Sans ce garde-fou, une dimension
 * catégorielle (ex: canal) serait utilisée telle quelle comme axe X d'une courbe,
 * ce qui produit un tracé lissé absurde entre des catégories sans ordre naturel.
 * Le breakdown n'a de sens que si l'axe X déjà résolu est un vrai champ temps
 * (la granularité a fourni l'axe) et que la dimension candidate n'est ni cet axe
 * lui-même, ni un autre champ temporel (auquel cas elle jouerait déjà ce rôle).
 */
export function resolveBreakdownKey(
  dimKey: string | undefined,
  explicitDimensions: string[] | undefined,
): string | undefined {
  if (!explicitDimensions || !explicitDimensions.length) return undefined;
  if (!isTimeDimensionKey(dimKey)) return undefined;
  const candidate = explicitDimensions[0];
  if (candidate === dimKey) return undefined;
  if (isTimeDimensionKey(candidate)) return undefined;
  return candidate;
}

/** Une série pivotée pour une valeur de breakdown donnée (ex: un canal). */
export interface PivotedSeries {
  key: string;
  data: number[];
}

/** Résultat du pivot : dates uniques triées + une série par valeur de breakdown. */
export interface PivotResult {
  dates: string[];
  series: PivotedSeries[];
}

/**
 * Pivote des lignes plates `{[timeKey]: string, [breakdownKey]: string, [metric]: number}[]`
 * en une série par valeur de `breakdownKey`, alignée sur les dates uniques triées.
 * Une valeur manquante pour une date donnée (ex: canal absent ce jour-là) devient 0
 * plutôt qu'un trou, pour que chaque série garde la même longueur que `dates`.
 * Les séries sont triées par volume total décroissant (les plus grosses courbes en premier).
 */
export function pivotSeriesByBreakdown(
  rows: Row[],
  timeKey: string,
  breakdownKey: string,
  metric: string,
): PivotResult {
  if (!rows.length) return { dates: [], series: [] };

  const dates = Array.from(new Set(rows.map((r) => String(r[timeKey])))).sort();
  const dateIndex = new Map(dates.map((d, i) => [d, i]));

  const valuesByKey = new Map<string, number[]>();
  for (const row of rows) {
    const breakdownValue = String(row[breakdownKey]);
    const date = String(row[timeKey]);
    const idx = dateIndex.get(date);
    if (idx === undefined) continue;
    let data = valuesByKey.get(breakdownValue);
    if (!data) {
      data = new Array(dates.length).fill(0);
      valuesByKey.set(breakdownValue, data);
    }
    data[idx] = Number(row[metric]) || 0;
  }

  const series = Array.from(valuesByKey.entries()).map(([key, data]) => ({ key, data }));
  series.sort((a, b) => {
    const totalA = a.data.reduce((sum, v) => sum + v, 0);
    const totalB = b.data.reduce((sum, v) => sum + v, 0);
    return totalB - totalA;
  });

  return { dates, series };
}
